<template>
  <BaseTemplate app-name="Sistema RIS/PACS" page-title="">
    <template #content>
      <div class="flex flex-col h-full">
        <!-- Filtros superiores -->
        <div class="bg-white dark:bg-gray-900 p-4 rounded-xl mb-6 shadow border border-gray-200 dark:border-gray-700">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-2 items-end">
            <div class="flex flex-col gap-1 min-w-0 lg:col-span-1">
              <label class="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Período</label>
              <div class="flex items-center gap-2 min-w-0">
                <input type="date" class="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white shadow-sm hover:shadow-md transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-gray-700 dark:text-gray-200" 
                       :style="{ 
                         '--tw-ring-color': clinicColors.primary,
                         '--tw-border-opacity': '1',
                         'border-color': clinicColors.primary
                       }"
                       :class="`focus:ring-2 focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`"
                       v-model="filtros.dataInicio"
                       autocomplete="off"
                       data-form-type="other" />
                <span class="mx-1 text-gray-400 dark:text-gray-500 font-medium">a</span>
                <input type="date" class="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white shadow-sm hover:shadow-md transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-gray-700 dark:text-gray-200" 
                       :style="{ 
                         '--tw-ring-color': clinicColors.primary,
                         '--tw-border-opacity': '1',
                         'border-color': clinicColors.primary
                       }"
                       :class="`focus:ring-2 focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`"
                       v-model="filtros.dataFim"
                       autocomplete="off"
                       data-form-type="other" />
              </div>
            </div>
            <div class="flex flex-col gap-1 min-w-0 w-full max-w-[90px] lg:col-span-1">
              <label class="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Modalidade</label>
              <div class="relative min-w-0">
                <button class="w-full border rounded-lg px-3 py-2 text-sm text-white flex items-center justify-between focus:ring-2 min-w-0 modalidades-dropdown text-center shadow-md hover:shadow-lg transition-all duration-200" 
                        :style="{ 
                          'border-color': clinicColors.primary,
                          'background': `linear-gradient(to right, ${clinicColors.primary}, ${clinicColors.secondary})`,
                          '--tw-ring-color': clinicColors.primary
                        }"
                        :class="`focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:from-[${clinicColors.secondary}] hover:to-[${clinicColors.primary}]`"
                        @click.stop="toggleModalidades">
                  <span class="truncate w-full text-center font-medium text-white">
                    {{ (modalidades.find(m => m.valor === filtros.modalidadeSelecionada) || modalidades[0]).nome }}
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0 text-white transition-transform duration-200" :class="{'rotate-180': mostrarModalidades}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="mostrarModalidades" class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 modalidades-dropdown backdrop-blur-sm">
                  <div class="p-1">
                    <div 
                      v-for="modalidade in modalidades" 
                      :key="modalidade.valor"
                      @click.stop="selecionarModalidade(modalidade.valor)"
                      class="px-3 py-2 rounded-md cursor-pointer text-sm transition-all duration-200 text-center font-medium"
                      :style="modalidade.valor === filtros.modalidadeSelecionada ? 
                        { 
                          'background': `linear-gradient(to right, ${clinicColors.primary}, ${clinicColors.secondary})`,
                          'color': 'white',
                          'box-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        } : {}
                      "
                      :class="modalidade.valor === filtros.modalidadeSelecionada ? 'text-white shadow-md' : 'text-black dark:text-white hover:bg-gradient-to-r hover:text-white'"
                    >
                      {{ modalidade.nome }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col gap-1 min-w-0 lg:col-span-3 items-start">
              <label class="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 text-center">Pesquisar</label>
              <div class="relative flex min-w-0 max-w-md w-full">
                <button 
                  class="border-y border-l border-gray-300 dark:border-gray-700 rounded-l-lg px-3 py-2 text-white flex items-center justify-center transition-all duration-200 focus:outline-none hover:shadow-lg shadow-md"
                  :style="{ 
                    'background': `linear-gradient(to right, ${clinicColors.primary}, ${clinicColors.secondary})`
                  }"
                  :class="`hover:from-[${clinicColors.secondary}] hover:to-[${clinicColors.primary}]`"
                  @click="fetchPacientes"
                  aria-label="Buscar"
                >
                  <Search class="h-4 w-4" />
                </button>
                <input 
                  type="text" 
                  placeholder="Pesquisar" 
                  class="border-y border-gray-300 dark:border-gray-700 rounded-none px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-white flex-1 min-w-0 font-medium text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  :style="{ 
                    '--tw-ring-color': clinicColors.primary
                  }"
                  :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}]`"
                  v-model="filtros.pesquisa"
                  @keyup.enter="fetchPacientes"
                  autocomplete="off"
                  data-form-type="other"
                />
                <div class="relative numeros-dropdown">
                  <button class="border-y border-r rounded-r-lg px-3 py-2 text-sm text-white flex items-center justify-between focus:ring-2 min-w-0 shadow-md hover:shadow-lg transition-all duration-200" 
                          :style="{ 
                            'border-color': clinicColors.primary,
                            'background': `linear-gradient(to right, ${clinicColors.primary}, ${clinicColors.secondary})`,
                            '--tw-ring-color': clinicColors.primary
                          }"
                          :class="`focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:from-[${clinicColors.secondary}] hover:to-[${clinicColors.primary}]`"
                          @click.stop="toggleNumeros">
                    <span class="truncate font-medium text-white">{{ filtros.tipoPesquisa || 'Número' }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 flex-shrink-0 text-white transition-transform duration-200" :class="{'rotate-180': mostrarNumeros}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                  </button>
                  <div v-if="mostrarNumeros" class="absolute z-50 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 right-0 backdrop-blur-sm">
                    <div class="p-1">
                      <div 
                        v-for="opcao in opcoesNumero" 
                        :key="opcao.valor"
                        @click.stop="selecionarOpcaoNumero(opcao)"
                        class="px-3 py-2 rounded-md cursor-pointer text-sm transition-all duration-200 font-medium"
                        :style="opcao.nome === filtros.tipoPesquisa ? 
                          { 
                            'background': `linear-gradient(to right, ${clinicColors.primary}, ${clinicColors.secondary})`,
                            'color': 'white',
                            'box-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          } : {}
                        "
                        :class="opcao.nome === filtros.tipoPesquisa ? 'text-white shadow-md' : 'text-black dark:text-white hover:bg-gradient-to-r hover:text-white'"
                      >
                        {{ opcao.nome }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="flex flex-row gap-2 w-full md:w-auto justify-end items-end min-w-0 lg:col-span-1">
              <button 
                @click="viewMode = 'table'"
                class="px-3 py-2 text-sm font-medium rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-1 min-w-0 transition-colors"
                :style="viewMode === 'table' ? 
                  { 
                    'background-color': clinicColors.primary,
                    'color': 'white',
                    'border-color': clinicColors.primary,
                    '--tw-ring-color': clinicColors.primary
                  } : 
                  {
                    '--tw-ring-color': clinicColors.primary
                  }
                "
                :class="viewMode === 'table' ? 'ring-2' : ''"
              >
                <List class="h-5 w-5" />
              </button>
              <button 
                @click="viewMode = 'cards'"
                class="px-3 py-2 text-sm font-medium rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-1 min-w-0 transition-colors"
                :style="viewMode === 'cards' ? 
                  { 
                    'background-color': clinicColors.primary,
                    'color': 'white',
                    'border-color': clinicColors.primary,
                    '--tw-ring-color': clinicColors.primary
                  } : 
                  {
                    '--tw-ring-color': clinicColors.primary
                  }
                "
                :class="viewMode === 'cards' ? 'ring-2' : ''"
              >
                <LayoutGrid class="h-5 w-5" />
              </button>
              <button 
                class="p-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-1 min-w-0 transition-colors"
                :style="configuracaoAtiva ? 
                  { 
                    'background-color': clinicColors.primary,
                    'color': 'white',
                    'border-color': clinicColors.primary,
                    '--tw-ring-color': clinicColors.primary
                  } : 
                  {
                    '--tw-ring-color': clinicColors.primary
                  }
                "
                :class="configuracaoAtiva ? 'ring-2' : ''"
                @click="toggleConfiguracao"
              >
                <Settings class="h-5 w-5" />
              </button>
              <button v-if="isMedico" class="px-4 py-2 text-white rounded text-sm font-semibold shadow focus:ring-2 min-w-0"
                      :style="{ 
                        'background-color': clinicColors.primary,
                        '--tw-ring-color': clinicColors.primary
                      }"
                      :class="`hover:bg-[${clinicColors.secondary}]`"
                      @click="abrirModalRelatorios">
                Relatórios
              </button>
            </div>
          </div>
        </div>
        
                <!-- Painel de Configurações -->
        <transition name="configuracao" appear>
          <div v-if="configuracaoAtiva" class="bg-white dark:bg-gray-900 p-4 rounded-xl mb-4 shadow-lg border border-gray-200 dark:border-gray-700 backdrop-blur-sm">
            <!-- Cabeçalho com ícone e título -->
            <div class="flex justify-between items-center mb-4">
              <div class="flex items-center space-x-2">
                <div class="p-1.5 rounded-md shadow-sm"
                     :style="{ 
                       'background': `linear-gradient(to bottom right, ${clinicColors.primary}, ${clinicColors.secondary})`
                     }">
                  <Settings class="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 class="text-lg font-bold text-gray-800 dark:text-white">Configurações da Página</h3>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Personalize a visualização dos laudos</p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <button @click="salvarConfiguracoes" 
                  class="px-3 py-1.5 text-white rounded-md transition-all duration-300 transform hover:scale-105 shadow-sm flex items-center space-x-1.5 text-sm"
                  :style="{ 
                    'background': `linear-gradient(to right, ${clinicColors.primary}, ${clinicColors.secondary})`
                  }"
                  :class="`hover:from-[${clinicColors.secondary}] hover:to-[${clinicColors.primary}]`">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span class="font-medium">Salvar</span>
                </button>
                <button @click="resetarConfiguracoes" 
                  class="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-white rounded-md transition-all duration-300 transform hover:scale-105 shadow-sm flex items-center space-x-1.5 text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span class="font-medium">Resetar</span>
                </button>
                <button @click="configuracaoAtiva = false" 
                  class="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-all duration-300 transform hover:scale-110">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Grid de configurações -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <!-- Seção de Colunas Visíveis -->
              <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div class="flex items-center space-x-2 mb-3">
                  <div class="p-1 bg-blue-100 dark:bg-blue-900 rounded-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </div>
                  <h4 class="text-sm font-semibold text-gray-800 dark:text-white">Colunas Visíveis</h4>
                </div>
                <div class="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                  <label v-for="coluna in todasColunas" :key="coluna.id" 
                    class="flex items-center p-2 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 cursor-pointer group">
                    <input 
                      type="checkbox" 
                      :checked="laudoSettingsStore.isColunaVisivel(coluna.id)"
                      @change="laudoSettingsStore.setColunaVisivel(coluna.id, $event.target.checked)"
                      class="w-3.5 h-3.5 rounded border-gray-300 focus:ring-1 transition-all duration-200" 
                      :style="{ 
                        '--tw-ring-color': clinicColors.primary
                      }"
                      :class="`text-[${clinicColors.primary}] focus:ring-[${clinicColors.primary}]`"
                    />
                    <span class="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      {{ coluna.nome }}
                    </span>
                    <div class="ml-auto">
                      <span v-if="coluna.ordenavel" class="badge-ordenavel-compact">
                        Ordenável
                      </span>
                    </div>
                  </label>
                </div>
              </div>
              
              <!-- Seção de Ordenação -->
              <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div class="flex items-center space-x-2 mb-3">
                  <div class="p-1 bg-green-100 dark:bg-green-900 rounded-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                  </div>
                  <h4 class="text-sm font-semibold text-gray-800 dark:text-white">Ordenação Padrão</h4>
                </div>
                <div class="space-y-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Campo de Ordenação</label>
                    <select 
                      v-model="ordenacao.campo" 
                      @change="laudoSettingsStore.setOrdenacaoPadrao(ordenacao.campo, ordenacao.direcao)"
                      class="w-full p-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                      :style="{ 
                        '--tw-ring-color': clinicColors.primary
                      }"
                      :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`"
                    >
                      <option value="nome">Nome do Paciente</option>
                      <option value="numero">OS</option>
                      <option value="exame">Exame</option>
                      <option value="data">Data do Exame</option>
                      <option value="mod">Modalidade</option>
                      <option value="medRef">Médico</option>
                      <option value="laudo_medico">Laudado por</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Direção</label>
                    <select 
                      v-model="ordenacao.direcao" 
                      @change="laudoSettingsStore.setOrdenacaoPadrao(ordenacao.campo, ordenacao.direcao)"
                      class="w-full p-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                      :style="{ 
                        '--tw-ring-color': clinicColors.primary
                      }"
                      :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`"
                    >
                      <option value="asc">Crescente (A → Z)</option>
                      <option value="desc">Decrescente (Z → A)</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <!-- Seção de Paginação -->
              <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div class="flex items-center space-x-2 mb-3">
                  <div class="p-1 bg-purple-100 dark:bg-purple-900 rounded-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h4 class="text-sm font-semibold text-gray-800 dark:text-white">Paginação</h4>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Itens por Página</label>
                  <select v-model="itensPorPagina" 
                    class="w-full p-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                    :style="{ 
                      '--tw-ring-color': clinicColors.primary
                    }"
                    :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`">
                    <option :value="10">10 itens</option>
                    <option :value="25">25 itens</option>
                    <option :value="50">50 itens</option>
                    <option :value="100">100 itens</option>
                  </select>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Mostra {{ itensPorPagina }} exames por página
                  </p>
                </div>
              </div>
            </div>
            
            <!-- Informações adicionais -->
            <div class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div class="flex items-start space-x-2">
                <div class="p-1 bg-blue-100 dark:bg-blue-900 rounded-sm flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h5 class="text-xs font-medium text-blue-800 dark:text-blue-200">Dica</h5>
                  <p class="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                    Suas configurações são salvas automaticamente no navegador e serão mantidas entre as sessões. 
                    Use o botão "Resetar" para voltar às configurações padrão.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </transition>
        
        <!-- Tabela de pacientes -->
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-md overflow-hidden flex-grow">
          <!-- Loading state -->
          <div v-if="loadingPacientes" class="flex items-center justify-center py-12">
            <div class="text-center">
              <svg class="animate-spin h-8 w-8 mx-auto text-gray-400 dark:text-gray-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-gray-600 dark:text-gray-400">Carregando exames...</p>
            </div>
          </div>

          <!-- No data state -->
          <div v-else-if="!loadingPacientes && pacientes.length === 0" class="flex items-center justify-center py-12">
            <div class="text-center">
              <svg class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p class="text-gray-600 dark:text-gray-400 text-lg mb-2">Nenhum exame encontrado</p>
              <p class="text-gray-500 dark:text-gray-500 text-sm">Tente ajustar os filtros ou período de busca</p>
            </div>
          </div>

          <!-- Visualização em tabela -->
          <div v-else-if="viewMode === 'table'" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead class="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th v-for="coluna in colunasVisiveis" :key="coluna.id" class="px-3 py-2 text-xs uppercase font-medium text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap" :class="coluna.id === 'nome' || coluna.id === 'exame' ? 'text-left' : 'text-center'">
                    <div class="flex items-center" :class="coluna.id === 'nome' || coluna.id === 'exame' ? 'justify-start' : 'justify-center'">
                      {{ coluna.nome }}
                      <svg v-if="coluna.ordenavel" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 cursor-pointer text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" @click="ordenarPor(coluna.id)">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                <tr 
                  v-for="paciente in pacientesPaginados" 
                  :key="paciente.id"
                  @click="abrirModalAcoes(paciente)"
                  @contextmenu.prevent="abrirMenuSeries($event, paciente)"
                  class="cursor-pointer"
                  :class="[
                    {'bg-gray-100 dark:bg-gray-800': pacienteSelecionado?.id === paciente.id},
                    getStatusRowColor(paciente.status || 'sem_laudo', paciente.isEmergency)
                  ]"
                >
                  <td v-for="coluna in colunasVisiveis" :key="coluna.id" class="px-3 py-2 whitespace-nowrap text-sm text-gray-800 dark:text-gray-100" :class="coluna.id === 'nome' || coluna.id === 'exame' ? 'text-left' : 'text-center'">
                    <template v-if="coluna.id === 'nome'">
                      <div class="flex items-center justify-start">
                        <component :is="getStatusIcon(paciente.status || 'sem_laudo')" :class="'w-5 h-5 mr-2 ' + getStatusIconColor(paciente.status || 'sem_laudo')" />
                        <AlertTriangle v-if="paciente.urgencia && paciente.urgencia !== '0d' && paciente.urgencia !== null" :class="'w-4 h-4 mr-2 ' + getUrgenciaIconColor(paciente.urgencia)" />
                        <AlertTriangle v-if="paciente.isEmergency" class="w-4 h-4 mr-2 text-red-500 dark:text-red-400" title="Emergência" />
                        <span>
                          {{ paciente[coluna.id] }}
                        </span>
                      </div>
                    </template>
                    <template v-else-if="coluna.id === 'status'">
                      <div class="flex items-center justify-center">
                        <span :class="getStatusBadgeClass(paciente.status || 'sem_laudo')" class="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <component :is="getStatusIcon(paciente.status || 'sem_laudo')" class="h-3 w-3" />
                          {{ getStatusLabel(paciente.status || 'sem_laudo') }}
                        </span>
                      </div>
                    </template>
                    <template v-else-if="coluna.id === 'acoes'">
                    <div class="flex space-x-1 justify-center">
                        <button @click.stop="visualizarExame(paciente)" class="p-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded">
                          <Eye class="h-4 w-4" />
                      </button>
                        <button @click.stop="emitirLaudo(paciente)" class="p-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded">
                          <FileText class="h-4 w-4" />
                      </button>
                        <button @click.stop="baixarExame(paciente)" 
                                :disabled="downloadingImages"
                                :title="`Baixar todas as imagens DICOM do exame ${paciente.numero} em formato ZIP`"
                                class="p-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                          <Download v-if="!downloadingImages" class="h-4 w-4" />
                          <svg v-else class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                      </button>
                    </div>
                    </template>
                    <template v-else-if="coluna.id === 'data'">
                      {{ formatarData(paciente[coluna.id]) }}
                    </template>
                    <template v-else>
                      {{ paciente[coluna.id] }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Visualização em cards -->
          <div v-else-if="viewMode === 'cards'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            <div 
              v-for="paciente in pacientesPaginados" 
              :key="paciente.id"
              class="rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow cursor-pointer bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              @click="selecionarPaciente(paciente)"
              @contextmenu.prevent="abrirMenuSeries($event, paciente)"
            >
              <div class="mb-3">
                <div class="flex items-center space-x-2 mb-2">
                  <component :is="getStatusIcon(paciente.status || 'sem_laudo')" :class="'w-4 h-4 ' + getStatusIconColor(paciente.status || 'sem_laudo')" />
                  <AlertTriangle v-if="paciente.urgencia && paciente.urgencia !== '0d' && paciente.urgencia !== null" :class="'w-4 h-4 ' + getUrgenciaIconColor(paciente.urgencia)" />
                  <AlertTriangle v-if="paciente.isEmergency" class="w-4 h-4 text-red-500 dark:text-red-400" title="Emergência" />
                </div>
                <h3 class="font-medium text-left text-lg text-gray-900 dark:text-white">{{ paciente.nome }}</h3>
              </div>
              
              <div class="space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500 dark:text-gray-400">Status:</span>
                  <span :class="getStatusBadgeClass(paciente.status || 'sem_laudo')" class="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <component :is="getStatusIcon(paciente.status || 'sem_laudo')" class="h-3 w-3" />
                    {{ getStatusLabel(paciente.status || 'sem_laudo') }}
                  </span>
                </div>
                <div class="text-sm">
                  <div class="text-gray-500 dark:text-gray-400 mb-1">Exame:</div>
                  <div class="text-gray-900 dark:text-white text-left">{{ paciente.exame }}</div>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500 dark:text-gray-400">Data:</span>
                  <span class="text-gray-900 dark:text-white">{{ formatarData(paciente.data) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500 dark:text-gray-400">Modalidade:</span>
                  <span class="text-gray-900 dark:text-white">{{ paciente.mod }}</span>
                </div>
                <div v-if="paciente.procedencia" class="flex justify-between text-sm">
                  <span class="text-gray-500 dark:text-gray-400">Procedência:</span>
                  <span class="font-medium text-gray-900 dark:text-white">{{ paciente.procedencia }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500 dark:text-gray-400">Médico:</span>
                  <span class="text-gray-900 dark:text-white">{{ paciente.medRef }}</span>
                </div>
              </div>
              
              <div class="mt-4 flex justify-end space-x-2">
                <button @click.stop="visualizarExame(paciente)" class="p-1.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded">
                  <Eye class="h-4 w-4" />
                </button>
                <button @click.stop="emitirLaudo(paciente)" class="p-1.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded">
                  <FileText class="h-4 w-4" />
                </button>
                <button @click.stop="baixarExame(paciente)" 
                        :disabled="downloadingImages"
                        :title="`Baixar todas as imagens DICOM do exame ${paciente.numero} em formato ZIP`"
                        class="p-1.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                  <Download v-if="!downloadingImages" class="h-4 w-4" />
                  <svg v-else class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Paginação -->
          <div class="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-800 flex items-center justify-between">
            <!-- Debug temporário -->

            
            <div class="flex items-center text-sm text-gray-700 dark:text-gray-200">
              <select v-model="itensPorPagina" class="border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-gray-700 dark:text-gray-200"
                      :style="{ 
                        '--tw-ring-color': clinicColors.primary
                      }"
                      :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
              <span class="ml-2">Resultados por página</span>
            </div>
            
            <div class="flex items-center">
              <span v-if="totalRegistros > 0" class="text-sm text-gray-700 dark:text-gray-200 mr-4">
                Mostrando de {{ paginaInicial }} até {{ paginaFinal }} de {{ totalRegistros }}
              </span>
              <span v-else class="text-sm text-gray-500 dark:text-gray-400 mr-4">
                Nenhum resultado encontrado
              </span>
              <div v-if="totalPaginas > 1" class="flex">
                <button 
                  @click="irParaPagina(paginaAtual - 1)" 
                  class="px-2 py-1 border dark:border-gray-700 rounded-l-md bg-white dark:bg-gray-800 text-gray-700 dark:text-white"
                  :class="{'opacity-50 cursor-not-allowed': paginaAtual === 1}"
                  :disabled="paginaAtual === 1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  v-for="pagina in paginasVisiveis" 
                  :key="pagina"
                  @click="irParaPagina(pagina)"
                  class="px-3 py-1 border-t border-b dark:border-gray-700"
                  :style="paginaAtual === pagina ? 
                    { 
                      'background-color': clinicColors.primary,
                      'color': 'white'
                    } : 
                    {
                      'background-color': 'white',
                      'color': 'rgb(55 65 81)'
                    }
                  "
                  :class="paginaAtual === pagina ? 'text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-white'"
                >
                  {{ pagina }}
                </button>
                <button 
                  @click="irParaPagina(paginaAtual + 1)" 
                  class="px-2 py-1 border dark:border-gray-700 rounded-r-md bg-white dark:bg-gray-800 text-gray-700 dark:text-white"
                  :class="{'opacity-50 cursor-not-allowed': paginaAtual === totalPaginas}"
                  :disabled="paginaAtual === totalPaginas"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Modificar a seção de legenda para torná-la interativa -->
          <div class="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-800 flex flex-wrap gap-3 text-xs rounded-b-lg">
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('todos')">
              <Circle class="w-4 h-4 text-gray-400 dark:text-gray-500 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Todos</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('sem_laudo')">
              <FileText class="w-4 h-4 text-gray-500 dark:text-gray-200 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Sem laudo</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('pendente')">
              <Clock class="w-4 h-4 text-yellow-500 dark:text-yellow-300 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Pendente</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="toggleFiltroEmergencia">
              <AlertTriangle class="w-4 h-4 text-red-500 dark:text-red-400 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200" :class="{'font-bold text-red-700 dark:text-red-300': filtros.emergencia}">
                Emergência
              </span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('sendo_laudado')">
              <Loader class="w-4 h-4 text-green-500 dark:text-green-300 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Sendo laudado</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('laudado')">
              <CheckCircle class="w-4 h-4 text-green-500 dark:text-green-300 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Laudado</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('revisado')">
              <RefreshCcw class="w-4 h-4 text-violet-600 dark:text-violet-400 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Revisado</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('entregue')">
              <Truck class="w-4 h-4 text-indigo-500 dark:text-indigo-300 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Entregue</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('cancelado')">
              <XCircle class="w-4 h-4 text-red-500 dark:text-red-400 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Cancelado</span>
            </div>
            <div class="flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded transition" @click="filtrarPorStatus('recalled')">
              <Undo2 class="w-4 h-4 text-amber-500 dark:text-amber-400 mr-1" />
              <span class="ml-1 text-gray-700 dark:text-gray-200">Reconvocado</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Modal componentizado para laudos -->
      <LaudoModal 
        :is-visible="modalLaudo"
        :paciente="pacienteSelecionado"
        @close="fecharModalLaudo"
        @laudo-salvo="onLaudoSalvo"
        @laudo-finalizado="onLaudoFinalizado"
        @paciente-atualizado="onPacienteAtualizado"
        @abrir-modal-mascara="abrirModalMascara"
      />
      
      <!-- Modal para cadastro de máscara de laudo -->
      <div v-if="modalMascara" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">Nova Máscara de Laudo</h3>
            <button @click="fecharModalMascara" class="text-gray-500 hover:text-gray-700 dark:text-white dark:hover:text-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-1">Nome da Máscara</label>
              <input 
                type="text" 
                v-model="novaMascara.nome" 
                class="w-full p-2 border rounded-md bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="Ex: Raio-X de Tórax Normal"
                autocomplete="off"
                data-form-type="other"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-1">Tipo de Exame</label>
              <select 
                v-model="novaMascara.tipoExame" 
                class="w-full p-2 border rounded-md bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value="">Selecione o tipo</option>
                <option value="SHOULDER">SHOULDER</option>
                <option value="CSPINE">CSPINE</option>
                <option value="HIP">HIP</option>
                <option value="ELBOW">ELBOW</option>
                <option value="KNEE">KNEE</option>
                <option value="CHEST">CHEST</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-1">Conteúdo da Máscara</label>
              <textarea 
                v-model="novaMascara.conteudo" 
                rows="8"
                class="w-full p-2 border rounded-md resize-none bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="Digite o texto padrão da máscara..."
              ></textarea>
            </div>
            
            <div class="flex justify-end space-x-2 pt-4">
              <button 
                @click="fecharModalMascara" 
                class="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button 
                @click="salvarMascara" 
                class="px-4 py-2 text-white rounded-md transition-colors"
                :style="{ 
                  'background-color': clinicColors.secondary
                }"
                :class="`hover:bg-[${clinicColors.primary}]`"
              >
                Salvar Máscara
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Modal de ações para o paciente -->
      <div v-if="modalAcoes" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">Ações do Exame</h3>
            <button @click="fecharModalAcoes" class="text-gray-500 hover:text-gray-700 dark:text-white dark:hover:text-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div class="mb-4">
            <div class="flex items-center space-x-3 mb-3">
              <component :is="getStatusIcon(pacienteSelecionado?.status || 'sem_laudo')" :class="'w-5 h-5 ' + getStatusIconColor(pacienteSelecionado?.status || 'sem_laudo')" />
              <AlertTriangle v-if="pacienteSelecionado?.urgencia && pacienteSelecionado?.urgencia !== '0d' && pacienteSelecionado?.urgencia !== null" :class="'w-5 h-5 ' + getUrgenciaIconColor(pacienteSelecionado?.urgencia)" />
              <h4 class="font-semibold text-gray-900 dark:text-white">{{ pacienteSelecionado?.nome }}</h4>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              <strong>Exame:</strong> {{ pacienteSelecionado?.exame }}<br>
              <strong>OS:</strong> {{ pacienteSelecionado?.numero }}<br>
              <strong>Data:</strong> {{ formatarData(pacienteSelecionado?.data) }}<br>
              <strong>Modalidade:</strong> {{ pacienteSelecionado?.mod }}
            </p>
          </div>
          
          <div class="grid grid-cols-3 gap-3">
            <button 
              @click="executarAcao('visualizar')"
              class="flex flex-col items-center justify-center space-y-2 px-3 py-3 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-lg transition-colors"
            >
              <Eye class="h-5 w-5" />
              <span class="text-xs font-medium text-center">Visualizar</span>
            </button>
            
            <button 
              @click="executarAcao('laudar')"
              class="flex flex-col items-center justify-center space-y-2 px-3 py-3 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-800 dark:text-green-200 rounded-lg transition-colors"
            >
              <FileText class="h-5 w-5" />
              <span class="text-xs font-medium text-center">{{ isMedico ? 'Emitir Laudo' : 'Visualizar Laudo' }}</span>
            </button>
            
            <button 
              @click="executarAcao('baixar')"
              :disabled="downloadingImages"
              :title="`Baixar todas as imagens DICOM do exame ${pacienteSelecionado?.numero} em formato ZIP`"
              class="flex flex-col items-center justify-center space-y-2 px-3 py-3 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-800 dark:text-purple-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download v-if="!downloadingImages" class="h-5 w-5" />
              <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-xs font-medium text-center">{{ downloadingImages ? 'Baixando...' : 'Baixar' }}</span>
            </button>
          </div>
          
          <div class="mt-6 flex justify-end">
            <button 
              @click="fecharModalAcoes" 
              class="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-md transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
      
      <!-- Banner Message -->
      <BannerMessage
        v-if="banner.show"
        :type="banner.type"
        :message="banner.message"
        @close="banner.show = false"
      />
      
      <!-- Menu de contexto customizado para séries -->
      <div v-if="contextoSeries.visivel" :style="{ position: 'fixed', left: contextoSeries.x + 'px', top: contextoSeries.y + 'px', zIndex: 1000 }" class="bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-4 min-w-[240px] max-w-xs backdrop-blur-md">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2 font-bold text-gray-700 dark:text-gray-200">
            <Layers class="w-5 h-5" :style="{ color: clinicColors.primary }" />
            Séries do exame
          </div>
          <button @click.stop="fecharMenuSeries" class="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition p-1 rounded-full focus:outline-none">
            <X class="w-5 h-5" />
          </button>
        </div>
        <div v-if="contextoSeries.loading" class="text-gray-500 dark:text-gray-400 py-4 text-center">Carregando...</div>
        <div v-else-if="contextoSeries.erro" class="text-red-500 dark:text-red-400 py-4 text-center">{{ contextoSeries.erro }}</div>
        <ul v-else class="max-h-72 overflow-y-auto pr-1">
          <li v-for="serie in contextoSeries.series" :key="serie.series_instance_uid" class="flex justify-between items-center py-2 px-2 rounded-lg border-b last:border-b-0 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition cursor-pointer">
            <span class="truncate text-gray-700 dark:text-gray-200">{{ serie.series_description || 'Série' }}</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">{{ serie.num_images }} img</span>
          </li>
          <li v-if="contextoSeries.series.length === 0" class="text-gray-500 dark:text-gray-400 py-4 text-center">Nenhuma série encontrada</li>
        </ul>
      </div>

      <!-- Modal de relatórios de produtividade médica -->
      <div v-if="modalRelatorios" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <!-- Cabeçalho do modal -->
          <div class="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center space-x-3">
              <div class="p-2 rounded-lg shadow-sm"
                   :style="{ 
                     'background': `linear-gradient(to bottom right, ${clinicColors.primary}, ${clinicColors.secondary})`
                   }">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-800 dark:text-white">
                  {{ isMedico.value && !temTodasPermissoes.value ? 'Meu Relatório de Produtividade' : 'Relatório de Produtividade Médica' }}
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ isMedico.value && !temTodasPermissoes.value ? 'Configure os filtros para gerar seu relatório pessoal' : 'Configure os filtros para gerar o relatório' }}
                </p>
              </div>
            </div>
            <button @click="fecharModalRelatorios" class="text-gray-400 hover:text-gray-600 dark:text-white dark:hover:text-gray-300 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Conteúdo do modal -->
          <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <form @submit.prevent="gerarRelatorioProdutividade" class="space-y-6">
              <!-- Filtros do relatório -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Médico - Mostrar apenas para usuários com todas as permissões -->
                <div v-if="temTodasPermissoes">
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Médico <span class="text-gray-400">(opcional)</span>
                  </label>
                  <select v-model="relatorioForm.medico" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                          :style="{ 
                            '--tw-ring-color': clinicColors.primary
                          }"
                          :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`">
                    <option value="">Todos os médicos</option>
                    <option v-for="medico in medicos" :key="medico.id" :value="medico.id">{{ medico.name }}</option>
                  </select>
                </div>
                
                <!-- Informação para médicos sem todas as permissões -->
                <div v-else-if="isMedico" class="md:col-span-2">
                  <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div class="flex items-start space-x-3">
                      <div class="p-1 bg-blue-100 dark:bg-blue-900 rounded-sm flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 class="text-sm font-medium text-blue-800 dark:text-blue-200">Relatório Pessoal</h4>
                        <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          Este relatório mostrará apenas seus dados de produtividade médica. 
                          Para acessar relatórios de outros médicos, entre em contato com o administrador.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Agenda -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Agenda <span class="text-gray-400">(opcional)</span>
                  </label>
                  <select v-model="relatorioForm.agenda" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                          :style="{ 
                            '--tw-ring-color': clinicColors.primary
                          }"
                          :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`">
                    <option value="">Todas as agendas</option>
                    <option v-for="agenda in agendas" :key="agenda.id" :value="agenda.id">{{ agenda.name }}</option>
                  </select>
                </div>
                
                <!-- Período -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Data Início</label>
                  <input type="date" v-model="relatorioForm.data_inicio" 
                         class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                         :style="{ 
                           '--tw-ring-color': clinicColors.primary
                         }"
                         :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`"
                         autocomplete="off"
                         data-form-type="other" />
                </div>
                
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Data Fim</label>
                  <input type="date" v-model="relatorioForm.data_fim" 
                         class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                         :style="{ 
                           '--tw-ring-color': clinicColors.primary
                         }"
                         :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`"
                         autocomplete="off"
                         data-form-type="other" />
                </div>
                
                <!-- Status do laudo -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Status do Laudo <span class="text-gray-400">(opcional)</span>
                  </label>
                  <select v-model="relatorioForm.status" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                          :style="{ 
                            '--tw-ring-color': clinicColors.primary
                          }"
                          :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`">
                    <option value="">Todos os status</option>
                    <option value="pending">Pendente</option>
                    <option value="completed">Concluído</option>
                    <option value="finalized">Finalizado</option>
                    <option value="signed">Assinado</option>
                  </select>
                </div>
                
                <!-- Tipo de emissão -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Tipo de Emissão</label>
                  <select v-model="relatorioForm.tipo_emissao" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                          :style="{ 
                            '--tw-ring-color': clinicColors.primary
                          }"
                          :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`">
                    <option value="analitico">Analítico (Detalhado)</option>
                    <option value="sintetico">Sintético (Resumido)</option>
                  </select>
                </div>
                
                <!-- Orientação do PDF -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Orientação do PDF</label>
                  <select v-model="relatorioForm.orientation" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-200"
                          :style="{ 
                            '--tw-ring-color': clinicColors.primary
                          }"
                          :class="`focus:ring-2 focus:ring-[${clinicColors.primary}] focus:border-[${clinicColors.primary}] hover:border-[${clinicColors.primary}]`">
                    <option value="portrait">Vertical (Retrato)</option>
                    <option value="landscape">Horizontal (Paisagem)</option>
                  </select>
                </div>
              </div>
              
              <!-- Informações do relatório -->
              <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div class="flex items-start space-x-3">
                  <div class="p-1 bg-blue-100 dark:bg-blue-900 rounded-sm flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-medium text-blue-800 dark:text-blue-200">
                      {{ isMedico.value && !temTodasPermissoes.value ? 'Sobre Seu Relatório' : 'Sobre o Relatório' }}
                    </h4>
                    <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      {{ isMedico.value && !temTodasPermissoes.value 
                        ? 'Este relatório mostra sua produtividade como médico radiologista, incluindo quantidade de laudos emitidos, tempo médio de conclusão e distribuição por status. O relatório será aberto em uma nova aba do navegador.'
                        : 'Este relatório mostra a produtividade dos médicos radiologistas, incluindo quantidade de laudos emitidos, tempo médio de conclusão e distribuição por status. O relatório será aberto em uma nova aba do navegador.'
                      }}
                    </p>
                  </div>
                </div>
              </div>
              
              <!-- Botões de ação -->
              <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="fecharModalRelatorios" 
                        class="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg transition-colors text-sm font-medium">
                  Cancelar
                </button>
                <button type="submit" :disabled="gerandoRelatorio"
                        class="px-6 py-2 text-white rounded-lg transition-all duration-300 text-sm font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        :style="{ 
                          'background': `linear-gradient(to right, ${clinicColors.primary}, ${clinicColors.secondary})`
                        }"
                        :class="`hover:from-[${clinicColors.secondary}] hover:to-[${clinicColors.primary}]`">
                  <svg v-if="gerandoRelatorio" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  <span>{{ gerandoRelatorio ? 'Gerando...' : 'Abrir Relatório' }}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import LaudoModal from '@/components/LaudoModal.vue';
import BannerMessage from '@/components/BannerMessage.vue';
import { 
  List,
  LayoutGrid, 
  Settings, 
  Eye, 
  FileText, 
  Download,
  X,
  Clock,
  Search,
  Circle,
  CheckCircle,
  RefreshCcw,

  AlertTriangle,
  Undo2,
  Loader,
  Layers,
  Truck,
  XCircle,
} from 'lucide-vue-next';
import apiClient from '@/services/authService';
import { useUserStore } from '@/stores/userStore';
import { useLaudoSettingsStore } from '@/stores/settings/laudoSettingsStore';
import { useClinicStore } from '@/stores/settings/clinicStore';

function formatDateToInput(date) {
  return date.toISOString().slice(0, 10);
}

const hoje = new Date();
const seisMesesAtras = new Date();
seisMesesAtras.setMonth(hoje.getMonth() - 6);

const filtros = ref({
  dataInicio: formatDateToInput(seisMesesAtras),
  dataFim: formatDateToInput(hoje),
  modalidadeSelecionada: '',
  tipoPesquisa: 'Número',
  pesquisa: '',
  statusSelecionado: 'todos', // Adicionado para controlar o status selecionado
  emergencia: false // Filtro para mostrar apenas emergências
});

// Store para configurações de laudo (deve ser inicializado primeiro)
const laudoSettingsStore = useLaudoSettingsStore();

// Store para configurações da clínica
const clinicStore = useClinicStore();

// Computed properties para cores da clínica
const clinicColors = computed(() => {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    primary: isDark ? clinicStore.clinic.cor_botao_dark : clinicStore.clinic.cor_botao,
    secondary: isDark ? clinicStore.clinic.cor_secundaria_dark : clinicStore.clinic.cor_secundaria,
    icon: isDark ? clinicStore.clinic.cor_icone_dark : clinicStore.clinic.cor_icone,
    font: isDark ? clinicStore.clinic.cor_fonte_dark : clinicStore.clinic.cor_fonte,
  };
});

const mostrarModalidades = ref(false);
const mostrarNumeros = ref(false);
const modalLaudo = ref(false);
const modalMascara = ref(false);
const modalAcoes = ref(false);
const modalRelatorios = ref(false);
const pacienteSelecionado = ref(null);
const configuracaoAtiva = ref(false);
const gerandoRelatorio = ref(false);
const itensPorPagina = computed({
  get: () => laudoSettingsStore.itensPorPagina,
  set: (value) => laudoSettingsStore.setItensPorPagina(value)
});

const paginaAtual = ref(1);
const ordenacao = ref({ 
  campo: laudoSettingsStore.ordenacaoPadrao.campo, 
  direcao: laudoSettingsStore.ordenacaoPadrao.direcao 
});

// Banner
const banner = ref({ show: false, type: 'success', message: '' });

// Loading
const loadingPacientes = ref(false);
const downloadingImages = ref(false);

// Dados de exemplo
const modalidades = ref([
  { nome: 'Todas', valor: '' },
  { nome: 'CT', valor: 'CT' },
  { nome: 'MG', valor: 'MG' },
  { nome: 'DX', valor: 'DX' },
  { nome: 'MR', valor: 'MR' },
  { nome: 'US', valor: 'US' },
  { nome: 'CR', valor: 'CR' },
  { nome: 'XA', valor: 'XA' },
  { nome: 'NM', valor: 'NM' },
  { nome: 'PT', valor: 'PT' },
  { nome: 'SC', valor: 'SC' },
]);

const opcoesNumero = ref([
  { nome: 'Número do exame', valor: 'exame' },
  { nome: 'ID do paciente', valor: 'paciente' },
  { nome: 'OS', valor: 'acesso' },
  { nome: 'Nome do paciente', valor: 'nome_paciente' }
]);

// Definir todas as colunas disponíveis
const todasColunas = [
  { id: 'nome', nome: 'Nome', ordenavel: true },
  { id: 'status', nome: 'Status', ordenavel: true },
  { id: 'numero', nome: 'OS', ordenavel: true },
  { id: 'exame', nome: 'Exame', ordenavel: true },
  { id: 'data', nome: 'Data', ordenavel: true },
  { id: 'mod', nome: 'Modalidade', ordenavel: true },
  { id: 'laudo_medico', nome: 'Laudado por', ordenavel: true },
  { id: 'medRef', nome: 'Méd. Ref.', ordenavel: true },

  { id: 'acoes', nome: 'Ações', ordenavel: false }
];

// Computed para obter colunas visíveis do store
const colunasVisiveis = computed(() => laudoSettingsStore.getColunasVisiveis);

// Substituir pacientes mockados por dados da API
const pacientes = ref([]);
const totalRegistrosAPI = ref(0); // Total de registros retornado pela API

// Controle de debounce para evitar múltiplas chamadas
const debounceTimer = ref(null);

// Computed properties para paginação (declaradas antes das funções que as usam)
const totalRegistros = computed(() => totalRegistrosAPI.value);
const totalPaginas = computed(() => Math.ceil(totalRegistros.value / itensPorPagina.value));
const paginaInicial = computed(() => {
  if (totalRegistros.value === 0) return 0;
  return (paginaAtual.value - 1) * itensPorPagina.value + 1;
});
const paginaFinal = computed(() => {
  if (totalRegistros.value === 0) return 0;
  return Math.min(paginaAtual.value * itensPorPagina.value, totalRegistros.value);
});

const paginasVisiveis = computed(() => {
  const totalDePaginas = totalPaginas.value;
  const paginaAtualNumero = paginaAtual.value;
  const maxPaginasVisiveis = 5;

  if (totalDePaginas <= 0) return [];
  if (totalDePaginas <= maxPaginasVisiveis) {
    return Array.from({ length: totalDePaginas }, (_, i) => i + 1);
  }

  let inicio = Math.max(1, paginaAtualNumero - Math.floor(maxPaginasVisiveis / 2));
  let fim = Math.min(totalDePaginas, inicio + maxPaginasVisiveis - 1);

  if (fim - inicio + 1 < maxPaginasVisiveis) {
    inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
  }

  return Array.from({ length: fim - inicio + 1 }, (_, i) => i + inicio);
});

const pacientesFiltradosEOrdenados = computed(() => {
  // Sem ordenação no cliente: retorna exatamente como veio do backend
  return pacientes.value;
});

const pacientesPaginados = computed(() => {
  // Sem paginação no cliente: backend já pagina e devolve a lista pronta para exibir
  return pacientesFiltradosEOrdenados.value;
});

// Função para resetar paginação
const resetarPagina = () => {
  paginaAtual.value = 1;
};

// Função para navegar para uma página específica
const irParaPagina = (pagina) => {
  if (pagina >= 1 && pagina <= totalPaginas.value) {
    paginaAtual.value = pagina;
  }
};

// Função para validar e corrigir página atual
const validarPaginaAtual = () => {
  if (totalPaginas.value > 0 && paginaAtual.value > totalPaginas.value) {
    paginaAtual.value = totalPaginas.value;
  }
  if (paginaAtual.value < 1) {
    paginaAtual.value = 1;
  }
};

// Função para buscar pacientes da API PACS com filtros e paginação
const fetchPacientes = async () => {
  // Evitar múltiplas chamadas simultâneas
  if (loadingPacientes.value) {
    console.log('Busca já em andamento, ignorando chamada duplicada');
    return;
  }

  loadingPacientes.value = true;
  try {
    const fieldMapping = {
      nome: 'patient_name',
      status: 'laudo_status',
      numero: 'accession_number',
      exame: 'study_description',
      data: 'study_date',
      mod: 'modality',
      laudo_medico: 'laudo_medico',
      medRef: 'referring_physician'
    };
    const orderingField = fieldMapping[ordenacao.value.campo] || ordenacao.value.campo;

    const params = {
      page: paginaAtual.value,
      page_size: itensPorPagina.value,
      ordering: `${ordenacao.value.direcao === 'desc' ? '-' : ''}${orderingField}`
    };
    
    if (filtros.value.pesquisa) {
      const tipoPesquisa = filtros.value.tipoPesquisa || 'exame';
      
      if (tipoPesquisa === 'nome_paciente') {
        params.patient_name = filtros.value.pesquisa;
      } else if (tipoPesquisa === 'exame') {
        params.study_description = filtros.value.pesquisa;
      } else if (tipoPesquisa === 'paciente') {
        params.patient_id = filtros.value.pesquisa;
      } else if (tipoPesquisa === 'acesso') {
        params.accession_number = filtros.value.pesquisa;
      } else {
        params.search = filtros.value.pesquisa;
      }
    }
    
    if (filtros.value.modalidadeSelecionada) params.modality = filtros.value.modalidadeSelecionada;
    if (filtros.value.dataInicio) params.data_inicio = filtros.value.dataInicio;
    if (filtros.value.dataFim) params.data_fim = filtros.value.dataFim;
    if (filtros.value.statusSelecionado && filtros.value.statusSelecionado !== 'todos') {
      params.laudo_status = filtros.value.statusSelecionado;
    }
    if (filtros.value.emergencia) {
      params.emergencia = true;
    }

    console.log('Buscando pacientes com parâmetros:', params);

    const response = await apiClient.get('/api/v1/pacs/studies', { params });
    const data = response.data;

    console.log('Resposta completa da API:', data);
    console.log('Tipo de resposta:', typeof data);
    console.log('É array?', Array.isArray(data));
    console.log('Tem results?', data.results ? 'Sim' : 'Não');
    console.log('Tem count?', data.count ? 'Sim' : 'Não');

    // Verificar se a resposta tem estrutura paginada
    if (data.results && Array.isArray(data.results)) {
      // Estrutura paginada padrão do Django
      pacientes.value = data.results;
      totalRegistrosAPI.value = data.count || data.total || 0;
      console.log('✅ Usando estrutura paginada:', { results: data.results.length, count: data.count });
    } else if (Array.isArray(data)) {
      // Estrutura simples (sem paginação no backend)
      pacientes.value = data;
      totalRegistrosAPI.value = data.length;
      console.log('⚠️ Usando estrutura simples (sem paginação):', { length: data.length });
    } else {
      // Fallback para outras estruturas
      pacientes.value = data.data || data;
      totalRegistrosAPI.value = data.count || data.total || (data.data ? data.data.length : 0);
      console.log('🔄 Usando fallback:', { data: data.data, count: data.count, total: data.total });
    }

    // Mapear estudos mantendo a ordem recebida da API (sem filtros ou ordenação no cliente)
    {
      const rawStudies = Array.isArray(pacientes.value) ? pacientes.value : [];
      pacientes.value = rawStudies.map((study) => ({
        id: study.accession_number || study.appointment_id || study.study_id || study.id || null,
        nome: study.patient_name || '-',
        exame: study.study_description || study.description || study.modality || '-',
        urgencia: study.urgencia ?? null,
        idade: study.patient_age || '',
        fin: study.fin || '-',
        entr: study.entr || '-',
        origem: study.procedencia || '-',
        sexo: study.patient_gender || 'O',
        numero: study.accession_number || '-',
        data: study.study_date || '-',
        mod: study.modality || '-',
        medRef: study.referring_physician || study.requesting_physician || '-',
        laudo_medico: study.laudo_medico || '-',
        idPaciente: study.patient_id || '-',
        ditadoPor: study.ditado_por || '-',
        laudadoPor: study.laudado_por || '-',
        numAcesso: study.accession_number || '-',
        dataNasc: study.patient_birth_date || '-',
        com: study.com || '-',
        esp: study.esp || '-',
        status: study.laudo_status || 'sem_laudo',
        statusOriginal: study.status || null,
        appointmentId: study.appointment_id,
        laudoId: study.laudo_id,
        imagemUrl: '/placeholder.svg?height=600&width=800',
        procedencia: study.procedencia || null,
        isEmergency: study.procedencia === 'Emergência'
      }));
    }

    console.log(`Pacientes carregados: ${pacientes.value.length}, Total: ${totalRegistrosAPI.value}`);

    // Log para debug da paginação
    console.log('Debug paginação:', {
      totalRegistrosAPI: totalRegistrosAPI.value,
      itensPorPagina: itensPorPagina.value,
      totalRegistros: totalRegistros.value,
      totalPaginas: totalPaginas.value,
      paginaAtual: paginaAtual.value,
      paginaInicial: paginaInicial.value,
      paginaFinal: paginaFinal.value,
      paginasVisiveis: paginasVisiveis.value
    });

    // Log específico para totalPaginas
    console.log('🔍 Análise totalPaginas:', {
      totalRegistros: totalRegistros.value,
      itensPorPagina: itensPorPagina.value,
      divisao: totalRegistros.value / itensPorPagina.value,
      totalPaginas: totalPaginas.value,
      deveMostrarPaginas: totalPaginas.value > 1
    });

  } catch (err) {
    console.error('Erro ao buscar exames:', err);
    const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erro ao buscar exames';
    showBanner('error', errorMessage);
    pacientes.value = [];
    totalRegistrosAPI.value = 0;
  } finally {
    loadingPacientes.value = false;
  }
};

// Função para buscar com debounce (para filtros)
const fetchPacientesComDebounce = () => {
  // Limpar timer anterior
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
  
  // Definir novo timer
  debounceTimer.value = setTimeout(() => {
    fetchPacientes();
  }, 300); // 300ms de debounce
};

// Buscar pacientes ao montar o componente e ao mudar filtros/paginação
onMounted(() => {
  // Inicializar clinicStore
  clinicStore.initialize();
  
  // Carregar configurações salvas
  laudoSettingsStore.carregarConfiguracoes();
  
  // Aplicar configurações carregadas
  ordenacao.value = {
    campo: laudoSettingsStore.ordenacaoPadrao.campo,
    direcao: laudoSettingsStore.ordenacaoPadrao.direcao
  };
  
  // Log para debug
  console.log('Configurações carregadas:', {
    colunasVisiveis: laudoSettingsStore.colunasVisiveis,
    colunasVisiveisComputed: laudoSettingsStore.getColunasVisiveis,
    ordenacao: laudoSettingsStore.ordenacaoPadrao,
    itensPorPagina: laudoSettingsStore.itensPorPagina,
    modoVisualizacao: laudoSettingsStore.modoVisualizacao
  });
  
  fetchPacientes();
});

// Watcher único para filtros (evita múltiplas chamadas)
watch([
  () => filtros.value.dataInicio,
  () => filtros.value.dataFim,
  () => filtros.value.modalidadeSelecionada,
  () => filtros.value.statusSelecionado,
  () => filtros.value.emergencia
], () => {
  console.log('Filtros alterados, aplicando debounce...');
  // Resetar para primeira página quando filtros mudarem
  resetarPagina();
  // Usar debounce para evitar múltiplas chamadas
  fetchPacientesComDebounce();
}, { immediate: false });

// Watcher para paginação (separado dos filtros)
watch([
  () => paginaAtual.value,
  () => itensPorPagina.value
], () => {
  console.log('Paginação alterada, verificando se deve buscar...');
  // Só buscar se não estiver carregando e se não for a primeira execução
  if (!loadingPacientes.value && pacientes.value.length > 0) {
    console.log('Buscando devido a mudança na paginação');
    fetchPacientes();
  } else {
    console.log('Ignorando busca - já carregando ou sem dados');
  }
}, { immediate: false });

// Watcher para aplicar estilos quando as cores da clínica mudarem
watch(clinicColors, () => {
  clinicStore.applyClinicStyles();
}, { deep: true });

// Watcher para mudanças nas colunas visíveis (reagir apenas à mudança de visibilidade)
watch(() => laudoSettingsStore.colunasVisiveis, () => {
  // Não precisa recarregar dados, apenas reagir à mudança de colunas
  console.log('Colunas visíveis atualizadas:', laudoSettingsStore.getColunasVisiveis);
}, { deep: true });

// Watcher para itens por página (resetar página mas não buscar imediatamente)
watch(() => itensPorPagina.value, () => {
  resetarPagina();
  // Salvar configuração no store
  laudoSettingsStore.setItensPorPagina(itensPorPagina.value);
  // Não buscar aqui - o watcher de paginação cuidará disso
});

// Função para formatar data (dd/MM/yyyy)
const formatarData = (dataStr) => {
  if (!dataStr) return '-';

  if (typeof dataStr === 'string') {
    // Tratar formato simples do backend: YYYY-MM-DD (evita problemas de fuso horário)
    const match = dataStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, y, m, d] = match;
      return `${d}/${m}/${y}`;
    }
  }

  const data = new Date(dataStr);
  if (isNaN(data)) return dataStr;
  const dia = String(data.getDate()).padStart(2, '0');
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const ano = data.getFullYear();
  return `${dia}/${mes}/${ano}`;
};

// Função para retornar o ícone de status
const getStatusIcon = (status) => {
  switch (status) {
    case 'sem_laudo':
      return FileText;
    case 'pendente':
      return Clock;
    case 'rascunho':
      return FileText;
    case 'sendo_laudado':
      return Loader;
    case 'laudado':
      return CheckCircle;
    case 'revisado':
      return RefreshCcw;
    case 'entregue':
      return Truck;
    case 'cancelado':
      return XCircle;
    case 'recalled':
      return Undo2;
    default:
      return FileText;
  }
};

// Função para retornar a cor do ícone de status - Paleta Moderna 2024
const getStatusIconColor = (status) => {
  switch (status) {
    case 'sem_laudo':
      return 'text-gray-500 dark:text-gray-200';
    case 'pendente':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'rascunho':
      return 'text-indigo-600 dark:text-indigo-400';
    case 'sendo_laudado':
      return 'text-amber-600 dark:text-amber-400';
    case 'laudado':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'revisado':
      return 'text-violet-600 dark:text-violet-400';
    case 'entregue':
      return 'text-blue-600 dark:text-blue-400';
    case 'cancelado':
      return 'text-red-600 dark:text-red-400';
    case 'recalled':
      return 'text-orange-600 dark:text-orange-400';
    default:
      return 'text-gray-500 dark:text-gray-200';
  }
};

// Função para retornar a cor do ícone de urgência
const getUrgenciaIconColor = (urgencia) => {
  if (!urgencia || urgencia === '0d' || urgencia === null) {
    return 'text-gray-400 dark:text-gray-500';
  }
  
  switch (urgencia) {
    case '1d': return 'text-orange-500 dark:text-orange-400';
    case '2d': return 'text-yellow-500 dark:text-yellow-300';
    case '3d': return 'text-red-500 dark:text-red-400';
    default: return 'text-gray-400 dark:text-gray-500';
  }
};

// Função para retornar a cor da linha baseada no status do laudo - Paleta Moderna 2024
const getStatusRowColor = (status = false) => {
  // Removida a tarja vermelha para emergência - mantém apenas o ícone
  
  switch (status) {
    case 'sem_laudo':
      return 'bg-white dark:bg-gray-900';
    case 'pendente':
      return 'bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-l-yellow-500';
    case 'rascunho':
      return 'bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-l-indigo-500';
    case 'sendo_laudado':
      return 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-l-amber-500';
    case 'laudado':
      return 'bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-l-emerald-500';
    case 'revisado':
      return 'bg-violet-50 dark:bg-violet-900/20 border-l-4 border-l-violet-500';
    case 'entregue':
      return 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500';
    case 'cancelado':
      return 'bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-500';
    case 'recalled':
      return 'bg-orange-50 dark:bg-orange-900/20 border-l-4 border-l-orange-500';
    default:
      return 'bg-white dark:bg-gray-900';
  }
};

// Função para retornar o label do status
const getStatusLabel = (status) => {
  switch (status) {
    case 'sem_laudo':
      return 'Sem Laudo';
    case 'pendente':
      return 'Pendente';
    case 'rascunho':
      return 'Rascunho';
    case 'sendo_laudado':
      return 'Sendo Laudado';
    case 'laudado':
      return 'Laudado';
    case 'revisado':
      return 'Revisado';
    case 'entregue':
      return 'Entregue';
    case 'cancelado':
      return 'Cancelado';
    case 'recalled':
      return 'Reconvocado';
    default:
      return 'Sem Laudo';
  }
};

// Função para retornar as classes do badge de status - Paleta Moderna 2024
const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'sem_laudo':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    case 'pendente':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'rascunho':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
    case 'sendo_laudado':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
    case 'laudado':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
    case 'revisado':
      return 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200';
    case 'entregue':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'cancelado':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'recalled':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

// Handlers de eventos
const toggleModalidades = () => {
  mostrarModalidades.value = !mostrarModalidades.value;
  // Fechar o dropdown de pesquisa quando abrir/fechar o dropdown de modalidades
  mostrarNumeros.value = false;
};

const toggleNumeros = () => {
  mostrarNumeros.value = !mostrarNumeros.value;
};

const selecionarModalidade = (modalidade) => {
  filtros.value.modalidadeSelecionada = modalidade;
  mostrarModalidades.value = false;
  // Fechar o dropdown de pesquisa quando selecionar uma modalidade
  mostrarNumeros.value = false;
};

const selecionarOpcaoNumero = (opcao) => {
  filtros.value.tipoPesquisa = opcao.nome;
  mostrarNumeros.value = false;
};

const selecionarPaciente = (paciente) => {
  pacienteSelecionado.value = paciente;
};

// Use a própria URL do frontend para abrir o viewer
const viewImage = (accessionNumber) => {
  const url = `/viewer/?accession_number=${accessionNumber}`;
  window.open(url, "_blank");
};

const visualizarExame = (paciente) => {
  selecionarPaciente(paciente);
  // Chame o visualizador externo em nova aba
  viewImage(paciente.numero);
  // Se quiser abrir o modal também, mantenha as linhas abaixo:
  // modalLaudo.value = true;
  // textoLaudo.value = '';
};

const emitirLaudo = (paciente) => {
  selecionarPaciente(paciente);
  // Se não tem appointmentId, tentar buscar ou criar agendamento
  if (!paciente.appointmentId) {
    // Usar o OS para buscar/criar agendamento
    paciente.numAcesso = paciente.numero;
  }
  modalLaudo.value = true;
};

const baixarExame = async (paciente) => {
  if (downloadingImages.value) return; // Evitar múltiplos downloads simultâneos
  
  downloadingImages.value = true;
  try {
    // Mostrar banner de carregamento
    showBanner('warning', `Preparando download das imagens do paciente ${paciente.nome}...`);
    
    // Fazer requisição para download das imagens em ZIP
    const response = await apiClient.get(
      `/api/v1/pacs/studies/${paciente.numero}/download-images/`,
      { 
        responseType: 'blob',
        timeout: 300000 // 5 minutos de timeout para arquivos grandes
      }
    );
    
    // Criar blob e fazer download
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extrair nome do arquivo do header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = `imagens_${paciente.nome.replace(/\s+/g, '_')}_${paciente.numero}.zip`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Limpar URL após download
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
    // Mostrar mensagem de sucesso
    showBanner('success', `Download das imagens do paciente ${paciente.nome} concluído com sucesso!`);
    
  } catch (error) {
    console.error('Erro ao baixar imagens:', error);
    
    let errorMessage = 'Erro ao baixar imagens do exame';
    
    if (error.response) {
      if (error.response.status === 404) {
        errorMessage = 'Nenhuma imagem encontrada para este exame';
      } else if (error.response.status === 403) {
        errorMessage = 'Sem permissão para baixar imagens deste exame';
      } else if (error.response.status === 500) {
        errorMessage = 'Erro interno do servidor ao processar imagens';
      } else {
        errorMessage = `Erro do servidor (${error.response.status})`;
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'Tempo limite excedido. O arquivo pode ser muito grande.';
    } else if (error.request) {
      errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
    }
    
    showBanner('error', errorMessage);
  } finally {
    downloadingImages.value = false;
  }
};

const fecharModalLaudo = () => {
  modalLaudo.value = false;
  modalAcoes.value = false; // Fechar também o modal de ações se estiver aberto
  
  // Recarregar exames sempre que fechar o modal
  fetchPacientes();
};

// Handlers para eventos do LaudoModal
const onLaudoSalvo = (laudo) => {
  console.log('Laudo salvo:', laudo);
  showBanner('success', 'Laudo salvo com sucesso!');
  // Aqui você pode atualizar a lista de pacientes
};

const onLaudoFinalizado = (laudo) => {
  console.log('Laudo finalizado:', laudo);
  showBanner('success', 'Laudo finalizado com sucesso!');
  
  // Atualizar status do paciente na lista
  if (pacienteSelecionado.value) {
    pacienteSelecionado.value.status = 'finalizado';
    
    // Atualizar na lista de pacientes
    const index = pacientes.value.findIndex(p => p.id === pacienteSelecionado.value.id);
    if (index !== -1) {
      pacientes.value[index].status = 'finalizado';
    }
  }
  
  // Fechar modal após finalizar
  modalLaudo.value = false;
};

const onPacienteAtualizado = (pacienteAtualizado) => {
  // Atualizar paciente na lista
  const index = pacientes.value.findIndex(p => p.id === pacienteAtualizado.id);
  if (index !== -1) {
    pacientes.value[index] = { ...pacientes.value[index], ...pacienteAtualizado };
  }
  
  // Atualizar paciente selecionado
  if (pacienteSelecionado.value && pacienteSelecionado.value.id === pacienteAtualizado.id) {
    pacienteSelecionado.value = { ...pacienteSelecionado.value, ...pacienteAtualizado };
  }
};

const ordenarPor = (campo) => {
  if (ordenacao.value.campo === campo) {
    ordenacao.value.direcao = ordenacao.value.direcao === 'asc' ? 'desc' : 'asc';
  } else {
    ordenacao.value.campo = campo;
    ordenacao.value.direcao = 'asc';
  }
  fetchPacientes();
};



const abrirModalMascara = () => {
  modalMascara.value = true;
  novaMascara.value = {
    nome: '',
    tipoExame: pacienteSelecionado.value ? pacienteSelecionado.value.exame : '',
    conteudo: ''
  };
};

const fecharModalMascara = () => {
  modalMascara.value = false;
};

const abrirModalAcoes = (paciente) => {
  selecionarPaciente(paciente);
  modalAcoes.value = true;
};

const fecharModalAcoes = () => {
  modalAcoes.value = false;
};

const toggleConfiguracao = () => {
  configuracaoAtiva.value = !configuracaoAtiva.value;
  // Aqui você pode adicionar lógica adicional para mostrar/esconder painel de configurações
  console.log('Configuração ativada:', configuracaoAtiva.value);
};

const resetarConfiguracoes = () => {
  laudoSettingsStore.resetarConfiguracoes();
  
  // Aplicar configurações resetadas
  ordenacao.value = {
    campo: laudoSettingsStore.ordenacaoPadrao.campo,
    direcao: laudoSettingsStore.ordenacaoPadrao.direcao
  };
  
  // Recarregar dados com as novas configurações
  fetchPacientes();
  
  showBanner('success', 'Configurações resetadas com sucesso!');
};

// Função para salvar configurações manualmente
const salvarConfiguracoes = () => {
  // Forçar salvamento das configurações atuais
  laudoSettingsStore.salvarConfiguracoes();
  
  showBanner('success', 'Configurações salvas com sucesso!');
};



const executarAcao = async (acao) => {
  if (!pacienteSelecionado.value) return;
  
  switch (acao) {
    case 'visualizar':
      visualizarExame(pacienteSelecionado.value);
      break;
    case 'laudar':
      if (isMedico.value) {
        emitirLaudo(pacienteSelecionado.value);
      } else {
        // Para não-médicos, abrir o modal de laudo em modo de visualização
        selecionarPaciente(pacienteSelecionado.value);
        modalLaudo.value = true;
      }
      break;
    case 'baixar':
      await baixarExame(pacienteSelecionado.value);
      break;
  }
  
  fecharModalAcoes();
};

const salvarMascara = () => {
  // Validação
  if (!novaMascara.value.nome || !novaMascara.value.tipoExame || !novaMascara.value.conteudo) {
    showBanner('error', 'Preencha todos os campos da máscara');
    return;
  }

  // Implementação para salvar máscara via API
  // TODO: Integrar com API do MaskLaudos
  console.log('Salvando máscara:', novaMascara.value);

  fecharModalMascara();
  // Aqui seria a chamada para a API
  showBanner('success', 'Máscara de laudo cadastrada com sucesso!');
};





// Função para filtrar por status baseado na legenda
const filtrarPorStatus = (status) => {
  console.log(`Filtrando por status: ${status}`);
  filtros.value.statusSelecionado = status;
  paginaAtual.value = 1; // Resetar para a primeira página ao aplicar filtro
};

// Função para alternar filtro de emergência
const toggleFiltroEmergencia = () => {
  filtros.value.emergencia = !filtros.value.emergencia;
  paginaAtual.value = 1; // Resetar para a primeira página ao aplicar filtro
};

const novaMascara = ref({
  nome: '',
  tipoExame: '',
  conteudo: ''
});

// Computed para verificar se o usuário é médico
const userStore = useUserStore();
const isMedico = computed(() => {
  const grupos = userStore.grupos || [];
  // Verificar se o usuário pertence a grupos de médicos
  return grupos.some(grupo =>
    grupo.toLowerCase().includes('medico') ||
    grupo.toLowerCase().includes('radiologista') ||
    grupo.toLowerCase().includes('médico') ||
    grupo.toLowerCase().includes('doctor')
  );
});

// Computed para verificar se o usuário tem todas as permissões
const temTodasPermissoes = computed(() => {
  const grupos = userStore.grupos || [];
  // Verificar se o usuário tem permissões administrativas ou de gestão
  return grupos.some(grupo =>
    grupo.toLowerCase().includes('admin') ||
    grupo.toLowerCase().includes('administrador') ||
    grupo.toLowerCase().includes('gerente') ||
    grupo.toLowerCase().includes('manager') ||
    grupo.toLowerCase().includes('supervisor') ||
    grupo.toLowerCase().includes('coordinator') ||
    grupo.toLowerCase().includes('diretor')
  );
});

// Estado adicional para o novo layout
const viewMode = computed({
  get: () => laudoSettingsStore.modoVisualizacao,
  set: (value) => laudoSettingsStore.setModoVisualizacao(value)
});

// Estado para menu de contexto
const contextoSeries = ref({
  visivel: false,
  x: 0,
  y: 0,
  series: [],
  loading: false,
  erro: '',
});

// Formulário para relatório de produtividade
const relatorioForm = ref({
  medico: '',
  agenda: '',
  data_inicio: '',
  data_fim: '',
  status: '',
  tipo_emissao: 'analitico',
  orientation: 'portrait'
});

// Listas para os selects do modal
const medicos = ref([]);
const agendas = ref([]);

// Função para abrir modal de relatórios
const abrirModalRelatorios = async () => {
  modalRelatorios.value = true;
  
  // Carregar dados necessários se ainda não foram carregados
  if (medicos.value.length === 0) {
    await carregarMedicos();
  }
  if (agendas.value.length === 0) {
    await carregarAgendas();
  }
  
  // Definir período padrão (últimos 30 dias)
  const hoje = new Date();
  const trintaDiasAtras = new Date();
  trintaDiasAtras.setDate(hoje.getDate() - 30);
  
  relatorioForm.value = {
    medico: isMedico.value && !temTodasPermissoes.value ? (medicos.value[0]?.id || '') : '',
    agenda: '',
    data_inicio: formatDateToInput(trintaDiasAtras),
    data_fim: formatDateToInput(hoje),
    status: '',
    tipo_emissao: 'analitico',
    orientation: 'portrait'
  };
};

// Função para fechar modal de relatórios
const fecharModalRelatorios = () => {
  modalRelatorios.value = false;
  gerandoRelatorio.value = false;
};

// Função para carregar médicos
const carregarMedicos = async () => {
  try {
    if (isMedico.value && !temTodasPermissoes.value) {
      // Se é médico sem todas as permissões, carregar apenas seus dados
      const response = await apiClient.get('/api/v1/profile/me/');
      const userData = response.data;
      
      // Criar objeto médico com dados do usuário logado
      medicos.value = [{
        id: userData.id,
        name: userData.name || userData.username || 'Médico',
        email: userData.email,
        is_current_user: true
      }];
      
      // Definir automaticamente o médico atual no formulário
      relatorioForm.value.medico = userData.id;
    } else {
      // Se tem todas as permissões, carregar todos os médicos
      const response = await apiClient.get('/api/v1/doctors/list/');
      medicos.value = response.data.results || response.data;
    }
  } catch (error) {
    console.error('Erro ao carregar médicos:', error);
    medicos.value = [];
  }
};

// Função para carregar agendas
const carregarAgendas = async () => {
  try {
    const response = await apiClient.get('/api/v1/schedules/list/');
    agendas.value = response.data.results || response.data;
  } catch (error) {
    console.error('Erro ao carregar agendas:', error);
    agendas.value = [];
  }
};

// Função para gerar relatório de produtividade
const gerarRelatorioProdutividade = async () => {
  try {
    gerandoRelatorio.value = true;
    
    // Garantir que médicos sem todas as permissões sempre tenham seu ID definido
    if (isMedico.value && !temTodasPermissoes.value && !relatorioForm.value.medico) {
      const response = await apiClient.get('/api/v1/profile/me/');
      relatorioForm.value.medico = response.data.id;
    }
    
    // Preparar dados do formulário
    const reportRequestData = {
      type: 'produtividade_medico',
      filters: {
        medico: relatorioForm.value.medico || null,
        agenda: relatorioForm.value.agenda || null,
        status: relatorioForm.value.status || null,
        data_inicio: relatorioForm.value.data_inicio || null,
        data_fim: relatorioForm.value.data_fim || null,
      },
      tipo_emissao: relatorioForm.value.tipo_emissao,
      orientation: relatorioForm.value.orientation,
      clinic_logo: clinicStore.clinic.logo
    };
    
    console.log('Enviando dados para relatório:', reportRequestData);
    
    // Chamar API para gerar relatório
    const response = await apiClient.post('/api/v1/reports/custom-reports/download-pdf/', reportRequestData, {
      responseType: 'blob'
    });
    
    console.log('Resposta recebida:', response);
    
    // Verificar se a resposta é um blob válido
    if (!response.data || response.data.size === 0) {
      throw new Error('PDF gerado está vazio ou inválido');
    }
    
    // Criar blob e abrir em nova aba
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Abrir PDF em nova aba
    const newWindow = window.open(url, '_blank');
    
    // Verificar se a nova aba foi aberta com sucesso
    if (newWindow) {
      // Limpar URL após um tempo para liberar memória
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
      const successMessage = isMedico.value && !temTodasPermissoes.value 
        ? 'Seu relatório de produtividade foi gerado e aberto em uma nova aba!'
        : 'Relatório gerado e aberto em uma nova aba!';
      
      showBanner('success', successMessage);
      fecharModalRelatorios();
    } else {
      // Se não conseguiu abrir nova aba (bloqueador de popup), tentar download
      const link = document.createElement('a');
      link.href = url;
      
      // Nome do arquivo baseado no tipo de usuário
      const fileName = isMedico.value && !temTodasPermissoes.value 
        ? `meu_relatorio_produtividade_${new Date().toISOString().slice(0, 10)}.pdf`
        : `relatorio_produtividade_medica_${new Date().toISOString().slice(0, 10)}.pdf`;
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showBanner('warning', 'Relatório baixado automaticamente. Verifique se o bloqueador de popups está desativado para abrir em nova aba.');
      fecharModalRelatorios();
    }
    
  } catch (error) {
    console.error('Erro detalhado ao gerar relatório:', error);
    
    let errorMessage = 'Erro ao gerar relatório';
    
    if (error.response) {
      // Erro de resposta do servidor
      console.log('Status da resposta:', error.response.status);
      console.log('Headers da resposta:', error.response.headers);
      
      if (error.response.status === 500) {
        // Tentar ler mensagem de erro do JSON
        try {
          const errorText = await error.response.data.text();
          console.log('Texto do erro:', errorText);
          
          // Tentar parsear como JSON
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || 'Erro interno do servidor';
          } catch (parseError) {
            // Se não for JSON, usar o texto como está
            errorMessage = errorText || 'Erro interno do servidor';
          }
        } catch (textError) {
          errorMessage = 'Erro interno do servidor';
        }
      } else if (error.response.status === 400) {
        errorMessage = 'Dados inválidos para gerar o relatório';
      } else if (error.response.status === 401) {
        errorMessage = 'Sessão expirada. Faça login novamente.';
      } else if (error.response.status === 403) {
        errorMessage = 'Sem permissão para gerar este relatório';
      } else {
        errorMessage = `Erro do servidor (${error.response.status})`;
      }
    } else if (error.request) {
      // Erro de rede
      errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
    } else {
      // Erro local
      errorMessage = error.message || 'Erro inesperado';
    }
    
    showBanner('error', errorMessage);
  } finally {
    gerandoRelatorio.value = false;
  }
};

// Função para buscar séries do exame
const buscarSeries = async (studyId) => {
  contextoSeries.value.loading = true;
  contextoSeries.value.erro = '';
  try {
    const response = await apiClient.get('/api/v1/pacs/series', { params: { study_id: studyId } });
    const data = response.data;
    contextoSeries.value.series = data;
  } catch (err) {
    console.error('Erro ao buscar séries:', err);
    const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erro ao buscar séries';
    contextoSeries.value.erro = errorMessage;
    contextoSeries.value.series = [];
  } finally {
    contextoSeries.value.loading = false;
  }
};

// Handler para clique direito
const abrirMenuSeries = async (event, paciente) => {
  event.preventDefault();
  contextoSeries.value.visivel = true;
  contextoSeries.value.x = event.clientX;
  contextoSeries.value.y = event.clientY;
  contextoSeries.value.series = [];
  await buscarSeries(paciente.numero || paciente.study_id || paciente.id);
};

const fecharMenuSeries = () => {
  contextoSeries.value.visivel = false;
};

// Fechar menu ao clicar fora
onMounted(() => {
  document.addEventListener('click', fecharMenuSeries);
});

// Handler para cliques fora dos dropdowns
const handleClickOutside = (e) => {
  const target = e.target;
  
  // Fechar dropdown de modalidades
  if (!target.closest('.modalidades-dropdown')) {
    mostrarModalidades.value = false;
  }
  
  // Fechar dropdown de números (opções de pesquisa)
  if (!target.closest('.numeros-dropdown')) {
    mostrarNumeros.value = false;
  }
};

// Handler para tecla Escape
const handleKeyDown = (e) => {
  if (e.key === 'Escape') {
    mostrarModalidades.value = false;
    mostrarNumeros.value = false;
  }
};

// Fechar dropdowns quando clicar fora
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeyDown);
});

// Cleanup listener on component unmount
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeyDown);
  
  // Limpar timer de debounce
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
});

// Função para exibir banners
function showBanner(type, message) {
  // Formata a mensagem para melhor apresentação
  let formattedMessage = message;
  
  // Se a mensagem vier do backend com detalhes, formata melhor
  if (typeof message === 'string' && message.includes('detail')) {
    // Remove prefixos comuns de erro do backend
    formattedMessage = message
      .replace(/^Erro ao .*?: /, '')
      .replace(/^Ocorreu um erro ao .*?: /, '')
      .replace(/^Erro: /, '')
      .replace(/^Erro desconhecido$/, 'Ocorreu um erro inesperado. Tente novamente.');
  }
  
  // Capitaliza a primeira letra se necessário
  if (formattedMessage && formattedMessage.length > 0) {
    formattedMessage = formattedMessage.charAt(0).toUpperCase() + formattedMessage.slice(1);
  }
  
  banner.value = { show: true, type, message: formattedMessage };
}

// Watcher para totalPaginas para validar página atual
watch(() => totalPaginas.value, () => {
  validarPaginaAtual();
});

// Watcher para aplicar estilos quando as cores da clínica mudarem
watch(clinicColors, () => {
  clinicStore.applyClinicStyles();
}, { deep: true });

// Watcher para mudanças nas colunas visíveis (reagir apenas à mudança de visibilidade)
watch(() => laudoSettingsStore.colunasVisiveis, () => {
  // Não precisa recarregar dados, apenas reagir à mudança de colunas
  console.log('Colunas visíveis atualizadas:', laudoSettingsStore.getColunasVisiveis);
}, { deep: true });

// Atualizar página ao trocar itens por página
watch(() => itensPorPagina.value, () => {
  resetarPagina();
  // Salvar configuração no store
  laudoSettingsStore.setItensPorPagina(itensPorPagina.value);
  // Buscar dados com nova configuração
  fetchPacientes();
});

</script>

<style scoped>
/* Melhorar contraste no modo claro */
.bg-yellow-100 {
  background-color: #fef3c7;
}

.bg-blue-100 {
  background-color: #dbeafe;
}

.bg-green-100 {
  background-color: #dcfce7;
}

.bg-emerald-100 {
  background-color: #d1fae5;
}

.bg-orange-100 {
  background-color: #fed7aa;
}

.bg-indigo-100 {
  background-color: #e0e7ff;
}

.bg-red-100 {
  background-color: #fee2e2;
}

/* Melhorar aparência dos botões de modo de visualização no modo claro */
button[class*="bg-[#00a99d]"] {
  border-color: var(--clinic-btn-bg) !important;
}

button[class*="bg-[#00a99d]"]:hover {
  background-color: var(--clinic-btn-bg-hover) !important;
  border-color: var(--clinic-btn-bg-hover) !important;
}

/* Garantir que o botão ativo tenha cores corretas */
button[class*="ring-[#00a99d]"][class*="bg-[#00a99d]"] {
  background-color: var(--clinic-btn-bg) !important;
  border-color: var(--clinic-btn-bg) !important;
  color: white !important;
}

/* Estilos específicos para o botão de configuração */
button[class*="configuracaoAtiva"] {
  background-color: var(--clinic-btn-bg) !important;
  border-color: var(--clinic-btn-bg) !important;
  color: white !important;
}

/* Hover para botão de configuração ativo */
button[class*="configuracaoAtiva"]:hover {
  background-color: var(--clinic-btn-bg-hover) !important;
  border-color: var(--clinic-btn-bg-hover) !important;
}

/* Estilos para botões com cores da clínica */
button[style*="background-color"] {
  transition: all 0.3s ease;
}

button[style*="background-color"]:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Estilos personalizados para o painel de configurações */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Estilos para modo escuro */
.dark .custom-scrollbar {
  scrollbar-color: #475569 #1e293b;
}

.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #1e293b;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Animações suaves para os elementos de configuração */
.configuracao-enter-active,
.configuracao-leave-active {
  transition: all 0.3s ease;
}

.configuracao-enter-from,
.configuracao-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Efeitos de hover melhorados para os checkboxes */
input[type="checkbox"]:checked {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
}

/* Gradientes personalizados para botões */
.btn-gradient {
  background: linear-gradient(135deg, var(--clinic-btn-bg) 0%, var(--clinic-btn-bg-hover) 100%);
  transition: all 0.3s ease;
}

.btn-gradient:hover {
  background: linear-gradient(135deg, var(--clinic-btn-bg-hover) 0%, var(--clinic-btn-bg) 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 169, 157, 0.3);
}

/* Efeitos de sombra melhorados */
.shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Animações para os ícones */
.icon-bounce {
  animation: iconBounce 0.6s ease-in-out;
}

@keyframes iconBounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -8px, 0);
  }
  70% {
    transform: translate3d(0, -4px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
}

/* Melhorar contraste dos elementos de configuração */
.bg-gray-50 {
  background-color: #f9fafb;
}

.dark .bg-gray-50 {
  background-color: #1f2937;
}

/* Efeitos de transição para os elementos de configuração */
.config-item {
  transition: all 0.2s ease-in-out;
}

.config-item:hover {
  transform: translateX(4px);
}

/* Estilos para os badges de "Ordenável" */
.badge-ordenavel {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #3730a3;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dark .badge-ordenavel {
  background: linear-gradient(135deg, #3730a3 0%, #312e81 100%);
  color: #e0e7ff;
}

/* Badge compacto para versão reduzida */
.badge-ordenavel-compact {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #3730a3;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 8px;
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dark .badge-ordenavel-compact {
  background: linear-gradient(135deg, #3730a3 0%, #312e81 100%);
  color: #e0e7ff;
}

/* Melhorar aparência dos selects */
select:focus {
  outline: none;
  border-color: var(--clinic-btn-bg);
  box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
}

/* Efeitos de hover para os cards de configuração */
.bg-gray-50:hover {
  background-color: #f3f4f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dark .bg-gray-50:hover {
  background-color: #374151;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Transições suaves para todos os elementos */
* {
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

/* Melhorar aparência dos inputs de data no modo claro */
input[type="date"]::-webkit-calendar-picker-indicator {
  background-color: transparent;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

input[type="date"]::-webkit-calendar-picker-indicator:hover {
  background-color: rgba(0, 169, 157, 0.1);
}

/* Estilos para inputs com cores da clínica */
input[style*="border-color"]:focus {
  border-color: var(--clinic-btn-bg) !important;
  box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1) !important;
}

/* Melhorar aparência dos dropdowns */
.modalidades-dropdown,
.numeros-dropdown {
  animation: dropdownFadeIn 0.2s ease-out;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Estilos específicos para o dropdown de números */
.numeros-dropdown .absolute {
  min-width: 200px;
  max-width: 250px;
}

/* Melhorar hover dos itens do dropdown */
.numeros-dropdown .absolute > div > div:hover,
.modalidades-dropdown .absolute > div > div:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Transições suaves para os dropdowns */
.numeros-dropdown button,
.modalidades-dropdown button {
  transition: all 0.2s ease;
}

.numeros-dropdown button:hover,
.modalidades-dropdown button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 169, 157, 0.3);
}

/* Efeitos de hover melhorados para os filtros */
.flex.flex-col.gap-1.min-w-0 input:hover,
.flex.flex-col.gap-1.min-w-0 button:hover {
  transform: translateY(-1px);
}

/* Melhorar contraste dos textos nos filtros */
.flex.flex-col.gap-1.min-w-0 label {
  font-weight: 600;
  letter-spacing: 0.025em;
}

/* Efeitos de foco melhorados */
.flex.flex-col.gap-1.min-w-0 input:focus,
.flex.flex-col.gap-1.min-w-0 button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
}

/* Melhorar aparência dos placeholders */
input::placeholder {
  font-weight: 400;
  opacity: 0.7;
}

/* Efeitos de gradiente para elementos ativos */
.bg-gradient-to-r.from-\[#00a99d\].to-\[#5fb3b3\] {
  background: linear-gradient(135deg, #00a99d 0%, #5fb3b3 100%);
  box-shadow: 0 2px 8px rgba(0, 169, 157, 0.3);
}

/* Melhorar aparência dos botões de filtro */
.flex.flex-col.gap-1.min-w-0 button {
  position: relative;
  overflow: hidden;
}

.flex.flex-col.gap-1.min-w-0 button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.flex.flex-col.gap-1.min-w-0 button:hover::before {
  left: 100%;
}

/* Melhorar aparência dos dropdowns no modo claro */
.modalidades-dropdown > div > div {
  position: relative;
  overflow: hidden;
}

.modalidades-dropdown > div > div::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 169, 157, 0.05) 0%, rgba(95, 179, 179, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.modalidades-dropdown > div > div:hover::before {
  opacity: 1;
}

/* Melhorar aparência dos selects nativos */
select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 32px;
}

select:focus {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2300a99d' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
}

/* Melhorar aparência dos options */
select option {
  background-color: white;
  color: #374151;
  font-weight: 500;
  padding: 8px 12px;
}

.dark select option {
  background-color: #374151;
  color: #f9fafb;
}

/* Efeitos de hover para os selects */
select:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Melhorar contraste dos labels */
label {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Efeitos de foco mais suaves */
input:focus,
select:focus,
button:focus {
  transform: scale(1.02);
}

/* Melhorar aparência dos inputs de data */
input[type="date"] {
  position: relative;
}

input[type="date"]::-webkit-datetime-edit {
  font-weight: 500;
  color: #374151;
}

.dark input[type="date"]::-webkit-datetime-edit {
  color: #f9fafb;
}

/* Efeitos de transição mais suaves */
.flex.flex-col.gap-1.min-w-0 * {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Melhorar aparência do botão de pesquisar */
.relative.flex.min-w-0.max-w-md.w-full button {
  position: relative;
  overflow: hidden;
  border-color: #00a99d;
}

.relative.flex.min-w-0.max-w-md.w-full button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s;
}

.relative.flex.min-w-0.max-w-md.w-full button:hover::before {
  left: 100%;
}

.relative.flex.min-w-0.max-w-md.w-full button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 169, 157, 0.4);
}

/* Estilos para botões com gradiente da clínica */
button[style*="linear-gradient"] {
  transition: all 0.3s ease;
}

button[style*="linear-gradient"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.relative.flex.min-w-0.max-w-md.w-full button:active {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(0, 169, 157, 0.3);
}

/* Melhorar aparência dos selects de modalidade e tipo de busca */
.modalidades-dropdown button,
.flex.flex-col.gap-1.min-w-0 button[class*="border-y"] {
  position: relative;
  overflow: hidden;
}

.modalidades-dropdown button::before,
.flex.flex-col.gap-1.min-w-0 button[class*="border-y"]::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s;
}

.modalidades-dropdown button:hover::before,
.flex.flex-col.gap-1.min-w-0 button[class*="border-y"]:hover::before {
  left: 100%;
}

.modalidades-dropdown button:hover,
.flex.flex-col.gap-1.min-w-0 button[class*="border-y"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 169, 157, 0.4);
}

.modalidades-dropdown button:active,
.flex.flex-col.gap-1.min-w-0 button[class*="border-y"]:active {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(0, 169, 157, 0.3);
}
</style>